import React, { useEffect, useMemo, useState } from 'react';
import {
    HiClipboardDocumentList,
} from 'react-icons/hi2';
import { TablePagination as Pagination } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
import { OrderService } from '../../service/manage/exam_subscription/order.service';
import downloadInvoicePDF from './InvoicePDf';
import { CombankService } from '../../service/manage/subscription/combank_pg.service';
import { useCart } from '../../context/cart/CartContext';
import { sweetStatusAlert } from '../../components/common/SweetAlert';
import { routes } from '../../helper/routes';
import OrdersTab from './OrdersTab';
import InvoicesTab from './InvoicesTab';
import './MyAccounts.css';
import './AccountSectionHeader.css';

const MyAccounts = ({ activeSection = 'orders' }) => {
    const [orders, setOrders] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [totalOrders, setTotalOrders] = useState(0);
    const [totalInvoices, setTotalInvoices] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [limit, setLimit] = useState(10);
    const [offset, setOffset] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [statusFilter, setStatusFilter] = useState('completed,failed');
    const { fetchCartItemCount } = useCart();
    const history = useHistory();
    const location = useLocation();
    const routeExpandInvoiceId = location.state?.expandInvoiceId || null;
    const [expandInvoiceId, setExpandInvoiceId] = useState(routeExpandInvoiceId);

    useEffect(() => {
        setExpandInvoiceId(routeExpandInvoiceId);
    }, [routeExpandInvoiceId]);

    const handlePaymentResponse = () => {
        const query = new URLSearchParams(window.location.search);
        const success = query.get('success');
        const cancel = query.get('cancel');
        const timeout = query.get('timeout');

        if (success === 'true') {
            setLoading(true);
            CombankService.retrieveOrderForCartPurchase(sessionStorage.getItem('checkout_session_id'))
                .then((response) => {
                    sessionStorage.removeItem('checkout_session_id');
                    query.delete('success');
                    sweetStatusAlert('Success!', 'Your payment was successful!', 'success').then(() => {
                        fetchCartItemCount();
                        updateUrl(query);
                        fetchAccountDetailsAfterPayment();
                    });
                })
                .catch(() => {
                    query.delete('success');
                    sweetStatusAlert('Error', 'Failed to verify payment. Please contact support.', 'error');
                    updateUrl(query);
                    setLoading(false);
                });
        } else if (cancel === 'true') {
            sessionStorage.removeItem('checkout_session_id');
            query.delete('cancel');
            sweetStatusAlert('', 'You are back to your cart!', 'info');
            updateUrl(query);
            setLoading(false);
        } else if (timeout === 'true') {
            sessionStorage.removeItem('checkout_session_id');
            query.delete('timeout');
            sweetStatusAlert('Timeout!', 'Your payment session timed out. Please try again.', 'warning');
            updateUrl(query);
            setLoading(false);
        } else {
            setLoading(false);
        }
    };

    const updateUrl = (query) => {
        const newQueryString = query.toString();
        const newUrl = newQueryString ? window.location.pathname + '?' + newQueryString : window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
    };

    const fetchAccountDetailsAfterPayment = async () => {
        setError(null);
        try {
            const userId = sessionStorage.getItem('manage_user_id');
            const userType = sessionStorage.getItem('userType');
            if (!userId) {
                setError('User session not found.');
                setLoading(false);
                return;
            }
            const response = await OrderService.getOrderDetails(userId, limit, offset, statusFilter, userType);
            if (response.success) {
                setOrders(response.data.orders || []);
                setTotalOrders(response.data.total_orders || 0);
            } else {
                setError(response.message || 'Failed to load account details.');
            }
        } catch (err) {
            setError('Failed to load account details. Please try again.');
            console.error('Account details fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchOrdersData = async () => {
        try {
            const userId = sessionStorage.getItem('manage_user_id');
            const userType = sessionStorage.getItem('userType');
            if (!userId) {
                setError('User session not found.');
                return;
            }
            const response = await OrderService.getOrderDetails(userId, limit, offset, statusFilter, userType);
            if (response.success) {
                setOrders(response.data.orders || []);
                setTotalOrders(response.data.total_orders || 0);
            } else {
                setError(response.message || 'Failed to load orders.');
            }
        } catch (err) {
            setError('Failed to load orders. Please try again.');
            console.error('Orders fetch error:', err);
        }
    };

    const fetchInvoicesData = async () => {
        try {
            const userId = sessionStorage.getItem('manage_user_id');
            const userType = sessionStorage.getItem('userType');
            if (!userId) {
                setError('User session not found.');
                return;
            }
            const response = await OrderService.getInvoiceDetails(userId, limit, offset, statusFilter, userType);
            if (response.success) {
                setInvoices(response.data.invoices || []);
                setTotalInvoices(response.data.total_invoices || 0);
            } else {
                setError(response.message || 'Failed to load invoices.');
            }
        } catch (err) {
            setError('Failed to load invoices. Please try again.');
            console.error('Invoices fetch error:', err);
        }
    };

    useEffect(() => {
        const fetchAccountDetails = async () => {
            setLoading(true);
            setError(null);
            
            try {
                const userId = sessionStorage.getItem('manage_user_id');
                const userType = sessionStorage.getItem('userType');
                if (!userId) {
                    setError('User session not found.');
                    setLoading(false);
                    return;
                }

                if (activeSection === 'orders') {
                    await fetchOrdersData();
                } else {
                    await fetchInvoicesData();
                }
            } catch (err) {
                setError('Failed to load details. Please try again.');
                console.error('Account details fetch error:', err);
            } finally {
                setLoading(false);
            }
        };

        // Check for payment response
        const query = new URLSearchParams(window.location.search);
        const hasPaymentResponse = query.get('success') || query.get('cancel') || query.get('timeout');
        
        if (hasPaymentResponse) {
            handlePaymentResponse();
        } else {
            fetchAccountDetails();
        }
    }, [offset, limit, statusFilter, activeSection]);

    useEffect(() => {
        setCurrentPage(0);
        setOffset(0);
    }, [activeSection]);

    const handlePageChange = (event, newPage) => {
        const newOffset = newPage * limit;
        setCurrentPage(newPage);
        setOffset(newOffset);
    };

    const handleRowsPerPageChange = (event) => {
        const newLimit = parseInt(event.target.value, 10);
        const newPage = Math.floor(offset / newLimit);
        setLimit(newLimit);
        setCurrentPage(newPage);
        setOffset(newLimit * newPage);
    };

    const handleDownloadInvoice = async (invoice) => {
        try {
            await downloadInvoicePDF(null, invoice, formatDate);
        } catch (err) {
            console.error('Failed to download invoice:', err);
            sweetStatusAlert('Error', 'Failed to download invoice', 'error');
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getStatusClass = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed': return 'completed';
            case 'pending': return 'pending';
            case 'cancelled': return 'cancelled';
            case 'processing': return 'processing';
            default: return 'pending';
        }
    };

    const getItemTypeClass = (type) => {
        return type === 'credit_package' ? 'credit_package' : 'assessment';
    };

    const getItemTypeLabel = (type) => {
        return type === 'credit_package' ? 'Credit Package' : 'Assessment';
    };

    const handleViewInvoice = (orderId) => {
        const matchingInvoice = invoices.find(inv => inv.order_id === orderId);
        history.push(routes.my_account_invoices, {
            expandInvoiceId: matchingInvoice?.invoice_id || null,
        });
    };

    const pageTitle = useMemo(() => {
        return activeSection === 'orders' ? 'Orders' : 'Invoices';
    }, [activeSection]);

    const pageSubtitle = useMemo(() => {
        if (activeSection === 'orders') {
            return `${totalOrders} ${totalOrders === 1 ? 'order' : 'orders'} in your purchase history`;
        }

        return `${totalInvoices} ${totalInvoices === 1 ? 'invoice' : 'invoices'} ready to review or download`;
    }, [activeSection, totalInvoices, totalOrders]);

    const totalCount = activeSection === 'orders' ? parseInt(totalOrders) : parseInt(totalInvoices);

    if (loading) {
        return (
            <div className="my-accounts-page">
                <div className="my-accounts-content">
                    <p className="my-accounts-loading">Loading account details...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="my-accounts-page">
                <div className="my-accounts-content">
                    <div className="my-accounts-empty-state">
                        <HiClipboardDocumentList className="my-accounts-empty-icon" />
                        <h3 className="my-accounts-empty-title">Unable to load</h3>
                        <p className="my-accounts-empty-message">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="my-accounts-page">
            <div className="account-section-page-header">
                <div className="account-section-page-header-left">
                    <h1 className="account-section-page-title">{pageTitle}</h1>
                    <p className="account-section-page-subtitle">{pageSubtitle}</p>
                </div>
            </div>

            {activeSection === 'orders' ? (
                <OrdersTab
                    orders={orders}
                    loading={loading}
                    error={error}
                    currentPage={currentPage}
                    limit={limit}
                    totalOrders={totalOrders}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    formatDate={formatDate}
                    getStatusClass={getStatusClass}
                    getItemTypeClass={getItemTypeClass}
                    getItemTypeLabel={getItemTypeLabel}
                    onViewInvoice={handleViewInvoice}
                />
            ) : (
                <InvoicesTab
                    invoices={invoices}
                    loading={loading}
                    error={error}
                    currentPage={currentPage}
                    limit={limit}
                    totalInvoices={totalInvoices}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    onDownloadInvoice={handleDownloadInvoice}
                    formatDate={formatDate}
                    getItemTypeClass={getItemTypeClass}
                    getItemTypeLabel={getItemTypeLabel}
                    expandInvoiceId={expandInvoiceId}
                />
            )}

            <div className="my-accounts-pagination">
                <Pagination
                    component="div"
                    count={totalCount}
                    page={currentPage}
                    rowsPerPage={limit}
                    rowsPerPageOptions={[5, 10, 20]}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                />
            </div>
        </div>
    );
};

export const MyOrdersPage = () => <MyAccounts activeSection="orders" />;
export const MyInvoicesPage = () => <MyAccounts activeSection="invoices" />;

export default MyOrdersPage;
